import { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";

import {
	BulkFindPersonResponse,
	BulkFindPersonSkippedRow,
} from "@/pages/Private/redux/list/list-find-person.api";

import { groupSkippedRows } from "./findPeopleDialog.util";

const PREFIX = "defineIcps.bulkFindPeople";

interface SkippedRowsListProps {
	/** The heading key, pluralised by the number of rows: before the run, or after it. */
	headingKey: "leftOut" | "skipped";
	rows?: BulkFindPersonSkippedRow[];
	/** Select all: the rows of the view that hold a person, counted by the API only. */
	ignoredRows?: number;
}

/**
 * The selected rows a bulk find person does not search, grouped by the reason the API gives, in
 * its words (in Ignored, in a campaign, already has a person, …), with how many rows each covers.
 */
export const SkippedRowsList: FunctionComponent<SkippedRowsListProps> = ({
	headingKey,
	rows,
	ignoredRows = 0,
}) => {
	const { t } = useTranslation();
	const groups = groupSkippedRows(rows);

	if (groups.length === 0 && ignoredRows === 0) {
		return null;
	}

	return (
		<div className="flex flex-col gap-1 text-ssm" data-testid={`skipped-rows-${headingKey}`}>
			{groups.length > 0 && (
				<>
					<span className="font-semibold">
						{t(`${PREFIX}.${headingKey}`, { count: rows?.length ?? 0 })}
					</span>
					<ul className="list-disc pl-5 text-text-meta">
						{groups.map(({ reason, count }) => (
							<li key={reason}>{t(`${PREFIX}.skippedReason`, { reason, count })}</li>
						))}
					</ul>
				</>
			)}
			{ignoredRows > 0 && (
				<span className="text-text-meta">{t(`${PREFIX}.ignoredRows`, { count: ignoredRows })}</span>
			)}
		</div>
	);
};

/** What the search started, as the API answered: the companies queued and the rows it skipped. */
export const FindPeopleResult: FunctionComponent<{ result: BulkFindPersonResponse }> = ({
	result,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col gap-3" data-testid="find-people-result">
			<p className="text-sm text-text">
				{result.queued > 0
					? t(`${PREFIX}.resultQueued`, { count: result.queued })
					: t(`${PREFIX}.resultNone`)}
			</p>
			<SkippedRowsList
				headingKey="skipped"
				ignoredRows={result.ignoredRows}
				rows={result.skippedRows}
			/>
		</div>
	);
};
